import { CommonModule, DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { LocationApiService } from '@xpo-ltl-2.0/sdk-location';
import { ShipmentApiService } from '@xpo-ltl-2.0/sdk-shipment';
import { DataApiService, NotificationService } from '@xpo-ltl/data-api';
import { DocumentManagementApiService } from '@xpo-ltl/sdk-documentmanagement';
import { LoggingApiService, SdkLoggingModule } from '@xpo-ltl/sdk-logging';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { NgxGalleryModule } from 'ngx-gallery-9';
import { NgxImageCompressService } from 'ngx-image-compress';
import { NgxSpinnerModule } from 'ngx-spinner';
// import { NavigatorService, XpoNavigatorModule } from '@xpo-ltl/navigator';
import { CONFIG_MANAGER_SERVICE_CONFIGURATION, ConfigManagerModule } from '@xpo-ltl/config-manager';

import { AngularFireModule } from '@angular/fire';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { XpoLtlAuthConfigLoaderService, XpoLtlAuthenticationService, XpoLtlAuthModule } from '@xpo-ltl/ngx-auth';
import { XpoBoardModule } from '@xpo-ltl/ngx-ltl-board';
import { XpoAgGridBoardModule } from '@xpo-ltl/ngx-ltl-board-grid';
import { XpoBadgeModule, XpoButtonModule, XpoCheckboxModule, XpoEmptyStateModule, XpoPageNotFoundRoutingModule, XpoTrayModule } from '@xpo-ltl/ngx-ltl-core';
import { XpoAgGridModule } from '@xpo-ltl/ngx-ltl-grid';
import { InfrastructureApiService } from '@xpo-ltl/sdk-infrastructure';
import { XPO_AUTH_CONFIG, XpoAuthConfig, XpoAuthenticationService } from '@xpo/ngx-auth';
import { environment } from '../environments/environment';
import { AppDialogsModule } from './app-dialogs.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppFooterComponent } from './components/app-footer/app-footer.component';
import { AppHeaderComponent } from './components/app-header/app-header.component';
import { EditSalvageRequestComponent } from './components/edit-salvage-request/edit-salvage-request.component';
import { ImageDetailsComponent } from './components/image-details/image-details.component';
import { ImageLabelDialogComponent } from './components/image-label-dialog/image-label-dialog.component';
import { ImageLandingComponent } from './components/image-landing/image-landing.component';
import { ImageSearchComponent } from './components/image-search/image-search.component';
import { SalvageRequestComponent } from './components/image-search/salvage-request/salvage-request.component';
import { ImageUploadTransformer } from './components/image-upload/image-upload-transformer/image-upload-transformer';
import { ImageUploadComponent } from './components/image-upload/image-upload.component';
import { NotAuthorizedComponent } from './components/not-authorized/not-authorized.component';
import { NotificationComponent } from './components/notification/notification.component';
import { CoreModule } from './core/core.module';
import { ConfirmCancelComponent } from './dialogs/confirm-cancel/confirm-cancel.component';
import { FeedbackImageComponent } from './dialogs/feedback-image/feedback-image/feedback-image.component';
import { SalvageFormSuccessDialogComponent } from './dialogs/salvage-form-success-dialog/salvage-form-success-dialog.component';
import { MaterialModule } from './material.module';
import { OsdXpoAuthenticationService, OsdXpoLtlAuthenticationService } from './ng-migration/osd-auth.service';
import { CommodityPackageCdPipe } from './pipes/commodity-package-cd.pipe';
import { DynamicProNumberFormatterPipe } from './pipes/dynamic-pro-number-formatter.pipe';
import { ProNumberFormatterPipe } from './pipes/pro-number-formatter.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { AppConstantsService } from './services/app-constants.service';
import { DmsApiService } from './services/dms-api/dms-api.service';
import { ImageUploadService } from './services/image-upload-service/image-upload.service';
import { SalvageRequestService } from './services/salvage-request-service/salvage-request.service';
import { DecimalNumberDirective } from './validators/decimal-number.directive';
import { EnforceAutocompleteSelectionDirective } from './validators/enforce-autocomplete-selection.directive';

export function authConfigFactory(configService: XpoLtlAuthConfigLoaderService): XpoAuthConfig {
  configService.initSettings('./assets/config.json');

  return new XpoAuthConfig({
    appCode: configService.appCode,
    scopes: configService.scopes,
    isProd: configService.isProd,
    applicationRootUri: configService.appRootUri,
  });
}

@NgModule({
  declarations: [
    AppComponent,
    SafeHtmlPipe,
    AppHeaderComponent,
    AppFooterComponent,
    ConfirmCancelComponent,
    NotAuthorizedComponent,
    ImageSearchComponent,
    ImageUploadComponent,
    ImageDetailsComponent,
    SalvageRequestComponent,
    ImageLabelDialogComponent,
    NotificationComponent,
    CommodityPackageCdPipe,
    ProNumberFormatterPipe,
    DynamicProNumberFormatterPipe,
    SalvageFormSuccessDialogComponent,
    ImageLandingComponent,
    FeedbackImageComponent,
    EditSalvageRequestComponent,
    EnforceAutocompleteSelectionDirective,
    DecimalNumberDirective,
  ],
  imports: [
    AngularFireModule.initializeApp(environment.firebase),
    BrowserModule,
    BrowserAnimationsModule,
    MaterialModule,
    ConfigManagerModule,
    NgxSpinnerModule,
    MatTooltipModule,
    SdkLoggingModule,
    XpoButtonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatBadgeModule,
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    XpoCheckboxModule,
    XpoBadgeModule,
    // XpoNavigatorModule,
    XpoBoardModule,
    XpoAgGridBoardModule,
    XpoAgGridModule,
    XpoEmptyStateModule,
    AppDialogsModule,
    CommonModule,
    NgxGalleryModule,
    NgxDaterangepickerMd,
    ServiceWorkerModule.register('./ngsw-worker.js', { enabled: false }),
    CoreModule,
    MatSidenavModule,
    XpoTrayModule,
    XpoLtlAuthModule.forRoot({
      provide: XPO_AUTH_CONFIG,
      useFactory: authConfigFactory,
      deps: [XpoLtlAuthConfigLoaderService],
    }),
    AppRoutingModule,
    XpoPageNotFoundRoutingModule,
  ],
  providers: [
    {
      provide: CONFIG_MANAGER_SERVICE_CONFIGURATION,
      useValue: { configFileUrl: './assets/config.json' },
    },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MatDialogRef, useValue: {} },
    AppConstantsService,
    ImageUploadTransformer,
    DataApiService,
    NotificationService,
    ShipmentApiService,
    DmsApiService,
    DocumentManagementApiService,
    LocationApiService,
    LoggingApiService,
    ImageUploadService,
    SalvageRequestService,
    InfrastructureApiService,
    NgxImageCompressService,
    DatePipe,
    {
      provide: XpoLtlAuthenticationService,
      useExisting: OsdXpoLtlAuthenticationService,
    },
    {
      provide: XpoAuthenticationService,
      useExisting: OsdXpoAuthenticationService,
    },
  ],
  bootstrap: [AppComponent],
  entryComponents: [ImageLabelDialogComponent, ImageDetailsComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(/*private navigatorService: NavigatorService*/) {
    // navigatorService.init();
  }
}
